import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, ArrowRight, Inbox } from "lucide-react";

const preview = (v) => (typeof v === "string" ? v : Array.isArray(v) ? `${v.length} item(s)` : "");

export default function OverrideReview() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/franchise-admin/overrides/review");
      setItems(data.items || []);
    } catch (e) { toast.error(errMsg(e, "Could not load the review queue.")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (o) => { setBusy(o.id); try { await api.post(`/franchise-admin/overrides/${o.id}/approve`, {}); toast.success("Approved & published."); await load(); } catch (e) { toast.error(errMsg(e, "Failed.")); } finally { setBusy(null); } };
  const reject = async (o) => { const r = window.prompt("Reason for changes?"); if (r === null) return; setBusy(o.id); try { await api.post(`/franchise-admin/overrides/${o.id}/reject`, { reason: r }); toast.success("Sent back."); await load(); } catch (e) { toast.error(errMsg(e, "Failed.")); } finally { setBusy(null); } };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="override-review-page">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} className="text-[#1C4358]" />
        <h1 className="text-xl font-display font-bold text-[#1C4358]">Content Review Queue</h1>
        <span className="text-[13px] text-[#7A8A99]">({items.length} pending)</span>
      </div>
      <p className="mt-1 text-[13px] text-[#4B5563]">Hybrid content awaiting Super Admin approval before it goes public.</p>

      {items.length === 0 ? (
        <div className="text-center py-20 text-[#4B5563]" data-testid="review-empty">
          <Inbox size={30} className="mx-auto text-[#9BB9CE]" />
          <p className="mt-3">Nothing pending review. You're all caught up.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((o) => (
            <Card key={o.id} className="p-4 border-[#E6EDF2]" data-testid={`review-item-${o.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <span className="font-display font-semibold text-[#1C4358] text-[15px]">{o.field_label}</span>
                  <span className="text-[13px] text-[#7A8A99] ml-2">{o.franchise_name}{o.service_lane ? ` · ${o.service_lane}` : ""}</span>
                </div>
                <span className="text-[11px] text-[#B7791F] font-bold">Pending review</span>
              </div>
              <p className="mt-2 text-[14px] text-[#252221] bg-[#F9FBFC] border border-[#1C4358]/10 rounded-lg p-3">{preview(o.proposed_local_value) || <span className="italic text-[#7A8A99]">empty</span>}</p>
              <p className="mt-1.5 text-[12px] text-[#7A8A99]">Submitted by {o.submitted_by_email}</p>
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" disabled={busy === o.id} onClick={() => approve(o)} data-testid={`review-approve-${o.id}`} className="gap-1 bg-[#2F7D5A] hover:bg-[#255346] text-white"><CheckCircle2 size={13} /> Approve</Button>
                <Button size="sm" variant="outline" disabled={busy === o.id} onClick={() => reject(o)} data-testid={`review-reject-${o.id}`} className="gap-1 border-[#D6E0E7] text-[#9C1E22]"><XCircle size={13} /> Request changes</Button>
                <Button size="sm" variant="ghost" onClick={() => navigate(`/franchise-admin/${o.franchise_id}/content`)} className="gap-1 text-[#1C4358] ml-auto">Open franchise <ArrowRight size={13} /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
